import Mention, { type MentionOptions } from '@tiptap/extension-mention';

import { isMentionAvailable, mentionDisplayLabel } from '../utils/mentions/mentionDisplay';
import { MENTION_CLASS_NAME, MENTION_UNAVAILABLE_CLASS_NAME } from '../utils/mentions/mentionStyle';
import type { MentionType } from '../utils/mentions/mentionType';

import { createMentionSuggestion, type MentionAttributes } from './_components/mentionSuggestion/mentionSuggestion';
import type { MentionSuggestion, MentionSuggestionsSource } from './CommunityPostComposer.type';

export interface MentionExtensionOptions {
  // Read on every `@`, so the host can switch spaces or turn the list off without rebuilding the editor.
  getSuggestions: () => MentionSuggestionsSource | undefined;
  // Shown for a mention whose member is gone, which the API sends with a null label.
  fallbackLabel: string;
  // Accessible name of the member list.
  listLabel: string;
}

// Registered in every community editor, with or without the member list: an editor that
// doesn't know the node loads the whole document empty, and saving it would wipe the content.
export const createMentionExtension = ({ getSuggestions, fallbackLabel, listLabel }: MentionExtensionOptions) =>
  Mention.extend<MentionOptions<MentionSuggestion, MentionAttributes>>({
    // Mention 2.27's own Backspace also deletes the character after the mention (it replaces the same range twice), so this one deletes only the node.
    addKeyboardShortcuts() {
      return {
        Backspace: () =>
          this.editor.commands.command(({ tr, state }) => {
            const { empty, $from } = state.selection;
            const node = $from.nodeBefore;
            if (!empty || node?.type.name !== this.name) return false;

            tr.delete($from.pos - node.nodeSize, $from.pos);
            return true;
          }),
      };
    },

    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-id'),
          renderHTML: (attributes) => ({ 'data-id': attributes.id }),
        },
        type: {
          default: 'member' satisfies MentionType,
          // An unknown kind stays as stored and renders as unavailable, so it survives an edit.
          parseHTML: (element) => element.getAttribute('data-mention-type') ?? 'member',
          renderHTML: (attributes) => ({ 'data-mention-type': attributes.type }),
        },
        label: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-label'),
          renderHTML: (attributes) => (attributes.label ? { 'data-label': attributes.label } : {}),
        },
        // Set by the API for a member who left; the save drops it with every attribute but id and type.
        inactive: {
          default: false,
          parseHTML: (element) => element.hasAttribute('data-mention-inactive'),
          renderHTML: (attributes) => (attributes.inactive ? { 'data-mention-inactive': '' } : {}),
        },
      };
    },
  }).configure({
    HTMLAttributes: { class: MENTION_CLASS_NAME },
    renderText: ({ node }) => mentionDisplayLabel(node.attrs, fallbackLabel),
    renderHTML: ({ options, node }) =>
      isMentionAvailable(node.attrs)
        ? ['span', options.HTMLAttributes, node.attrs.label]
        : [
            'span',
            { ...options.HTMLAttributes, class: MENTION_UNAVAILABLE_CLASS_NAME },
            mentionDisplayLabel(node.attrs, fallbackLabel),
          ],
    suggestion: createMentionSuggestion(getSuggestions, listLabel),
  });
